import { useEffect, useRef, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import { Button } from "primereact/button";
import localeText from "../../../../locale";
import AdminService from "../../../../service/AdminService";
import "../../../UserDashboard/components/UserDashboardHome/UserDashboardHome.css";

const CompletedWithdrawalsReport = () => {
  const [loading, setLoading] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [completedWithdrawals, setCompletedWithdrawals] = useState([]);

  const adminService = new AdminService();
  const toast = useRef(null);

  const formatDateForApi = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const [dateBegin, setDateBegin] = useState("2000-01-01");
  const [dateEnd, setDateEnd] = useState(formatDateForApi(new Date()));

  const getArrayResponse = (res) => {
    if (Array.isArray(res)) {
      return res;
    }

    if (res?.messageCode) {
      if (res.messageCode !== 200001) {
        toast.current.show({
          severity: "error",
          summary: localeText("error"),
          detail: res.message || localeText("errorFetchingData"),
          life: 6000,
        });
        return [];
      }

      return Array.isArray(res.result) ? res.result : [];
    }

    return Array.isArray(res?.result) ? res.result : [];
  };

  const formatAmount = (value, currency = "EUR") => {
    if (value === undefined || value === null || value === "") return "-";

    const localeMap = {
      pt: "pt-PT",
      it: "it-IT",
      es: "es-ES",
      en: "en-IE",
    };

    const currentLocale = localStorage.getItem("__extraconomy_locale") || "it";
    const numericValue = Number(value);

    if (!Number.isFinite(numericValue)) return String(value);

    return new Intl.NumberFormat(localeMap[currentLocale] || "it-IT", {
      style: "currency",
      currency,
    }).format(numericValue);
  };

  const handleUnauthorized = (error) => {
    if (error?.response?.status === 401 || error?.response?.status === 403) {
      localStorage.removeItem("__extraconomy_token");
      localStorage.removeItem("__extraconomy_username");
      window.location.replace("/login");
      return true;
    }

    return false;
  };

  const fetchData = async (dateBeginParam = dateBegin, dateEndParam = dateEnd) => {
    if (dateBeginParam > dateEndParam) {
      toast.current.show({
        severity: "warn",
        summary: localeText("error"),
        detail: "A data inicial deve ser menor ou igual a data final",
        life: 5000,
      });
      return;
    }

    try {
      setLoading(true);
      const res = await adminService.getCompletedWithdrawalsReport(
        localStorage.getItem("__extraconomy_token"),
        dateBeginParam,
        dateEndParam,
      );
      setCompletedWithdrawals(getArrayResponse(res));
    } catch (error) {
      if (handleUnauthorized(error)) return;

      toast.current.show({
        severity: "error",
        summary: localeText("error"),
        detail: localeText("errorFetchingData"),
        life: 6000,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleApplyFilter = () => {
    fetchData(dateBegin, dateEnd);
  };

  const handleDownloadPdf = async () => {
    try {
      if (dateBegin > dateEnd) {
        toast.current.show({
          severity: "warn",
          summary: localeText("error"),
          detail: "A data inicial deve ser menor ou igual a data final",
          life: 5000,
        });
        return;
      }

      setDownloadingPdf(true);
      const response = await adminService.getCompletedWithdrawalsReportPdf(
        localStorage.getItem("__extraconomy_token"),
        dateBegin,
        dateEnd,
      );

      if (response?.data) {
        const blob = new Blob([response.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        const dateStr = new Date().toISOString().split("T")[0];
        link.download = `completed-withdrawals-report-${dateStr}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        toast.current.show({
          severity: "success",
          summary: localeText("success"),
          detail: localeText("pdfDownloadedSuccessfully"),
          life: 3000,
        });
      }
    } catch (error) {
      if (handleUnauthorized(error)) return;

      toast.current.show({
        severity: "error",
        summary: localeText("error"),
        detail: localeText("errorDownloadingPdf"),
        life: 6000,
      });
    } finally {
      setDownloadingPdf(false);
    }
  };

  const emptyMessage = () => {
    if (loading) return null;

    return (
      <div className="p-text-center">
        <i className="pi pi-inbox" style={{ fontSize: "3rem" }}></i>
        <p className="text-400 mt-3">{localeText("noData")}</p>
      </div>
    );
  };

  const getNestedValue = (obj, path) => {
    if (!obj || !path) return undefined;
    return path.split(".").reduce((acc, key) => (acc ? acc[key] : undefined), obj);
  };

  const getFirstAvailable = (row, keys = []) => {
    for (let i = 0; i < keys.length; i += 1) {
      const value = getNestedValue(row, keys[i]);
      if (value !== undefined && value !== null && value !== "") {
        return value;
      }
    }

    return "-";
  };

  const destinationTypeBody = (row) => {
    const hasPaypal = !!getFirstAvailable(row, ["paypalEmailToTransfer", "paypalEmail"]);
    if (hasPaypal && getFirstAvailable(row, ["paypalEmailToTransfer", "paypalEmail"]) !== "-") {
      return "PayPal";
    }

    const hasIban = getFirstAvailable(row, ["iban", "bankIban"]) !== "-";
    if (hasIban) return "Bank";

    return "-";
  };

  const paymentBody = (row) => {
    const paypalEmail = getFirstAvailable(row, ["paypalEmailToTransfer", "paypalEmail"]);
    if (paypalEmail !== "-") return paypalEmail;

    return getFirstAvailable(row, ["accountOwner", "bankname", "paymentMethod", "destination"]);
  };

  const amountRequestedBody = (row) => {
    return formatAmount(
      getFirstAvailable(row, ["amountRequested", "amount", "requestedAmount"]),
      getFirstAvailable(row, ["currency", "coin", "currencyCode"]) || "EUR",
    );
  };

  const netAmountBody = (row) => {
    return formatAmount(
      getFirstAvailable(row, ["amountToDeposit", "netAmount", "amountNet"]),
      getFirstAvailable(row, ["currency", "coin", "currencyCode"]) || "EUR",
    );
  };

  const localeMap = {
    pt: "pt-PT",
    it: "it-IT",
    es: "es-ES",
    en: "en-IE",
  };

  const formatDate = (value) => {
    if (value === undefined || value === null || value === "") return "-";

    const date = new Date(Number(value));
    if (Number.isNaN(date.getTime())) return "-";

    const currentLocale = localStorage.getItem("__extraconomy_locale") || "it";
    return new Intl.DateTimeFormat(localeMap[currentLocale] || "it-IT").format(date);
  };

  const requestDateBody = (row) => {
    return formatDate(getFirstAvailable(row, ["dateRequest", "requestDate"]));
  };

  return (
    <div className="card border-1 border-300">
      <Toast ref={toast} />

      <div className="flex justify-content-between align-items-center my-4">
        <h3>{localeText("completedWithdrawalsReport")}</h3>
        <Button
          label={localeText("exportData")}
          icon="pi pi-download"
          onClick={handleDownloadPdf}
          loading={downloadingPdf}
          disabled={downloadingPdf || loading}
          className="p-button-sm"
        />
      </div>

      <hr />

      <div className="flex flex-wrap align-items-end gap-3 my-4">
        <div className="flex flex-column gap-2">
          <label htmlFor="dateBegin">{localeText("startDate")}</label>
          <input
            id="dateBegin"
            type="date"
            value={dateBegin}
            onChange={(event) => setDateBegin(event.target.value)}
            className="p-inputtext p-component"
          />
        </div>

        <div className="flex flex-column gap-2">
          <label htmlFor="dateEnd">{localeText("endDate")}</label>
          <input
            id="dateEnd"
            type="date"
            value={dateEnd}
            onChange={(event) => setDateEnd(event.target.value)}
            className="p-inputtext p-component"
          />
        </div>

        <Button
          label={localeText("filter")}
          icon="pi pi-filter"
          onClick={handleApplyFilter}
          disabled={loading}
          className="p-button-sm"
        />
      </div>

      <DataTable
        className="mt-4 mb-6 p-datatable-customers"
        value={completedWithdrawals}
        paginator
        responsiveLayout="scroll"
        loading={loading}
        dataKey="id"
        emptyMessage={emptyMessage()}
        paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
        currentPageReportTemplate={localeText("showingFirstToLast")}
        rows={10}
        rowsPerPageOptions={[10, 20, 50]}
      >
        <Column
          header={localeText("name")}
          body={(row) =>
            getFirstAvailable(row, ["firstname", "name", "user.firstname", "user.name"])
          }
          style={{ minWidth: "10rem" }}
        />
        <Column
          header={localeText("surname")}
          body={(row) =>
            getFirstAvailable(row, ["surname", "lastname", "user.surname", "user.lastname"])
          }
          style={{ minWidth: "10rem" }}
        />
        <Column
          header={localeText("email")}
          body={(row) =>
            getFirstAvailable(row, [
              "email",
              "user.email",
              "username",
              "user.username",
              "paypalEmailToTransfer",
            ])
          }
          style={{ minWidth: "16rem" }}
        />
        <Column
          header={localeText("completedWithdrawalsHeaderRequestDate")}
          body={requestDateBody}
          style={{ minWidth: "10rem" }}
        />
        <Column
          header={localeText("completedWithdrawalsColDestinationType")}
          body={destinationTypeBody}
          style={{ minWidth: "9rem" }}
        />
        <Column
          header={localeText("completedWithdrawalsColPayment")}
          body={paymentBody}
          style={{ minWidth: "16rem" }}
        />
        <Column
          header={"IBAN"}
          body={(row) => getFirstAvailable(row, ["iban", "bankIban"])}
          style={{ minWidth: "14rem" }}
        />
        <Column
          header={localeText("currency")}
          body={(row) => getFirstAvailable(row, ["currency", "coin", "currencyCode"])}
          style={{ minWidth: "7rem" }}
        />
        <Column
          header={localeText("completedWithdrawalsColRequestedValue")}
          body={amountRequestedBody}
          style={{ minWidth: "10rem" }}
        />
        <Column
          header={localeText("completedWithdrawalsColNetValue")}
          body={netAmountBody}
          style={{ minWidth: "10rem" }}
        />
      </DataTable>
    </div>
  );
};

export default CompletedWithdrawalsReport;
